// react
import { FC, useEffect, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// types
import { CustomColumnDefinition, CustomColumnType, CustomColumnValidation } from '../../../@types';
import { customColumnTypes } from '../../../@types/unions';

// mui
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	TextField,
	Typography,
} from '@mui/material';

// icons
import { Add as IconAdd, Close as IconClose } from '@mui/icons-material';

interface DialogCustomColumnProps {
	column: CustomColumnDefinition | null;
	onClose: () => void;
	onSave: (column: Omit<CustomColumnDefinition, 'id' | 'order'>) => void;
	open: boolean;
}

export const DialogCustomColumn: FC<DialogCustomColumnProps> = ({
	column,
	onClose,
	onSave,
	open,
}) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [name, setName] = useState('');
	const [type, setType] = useState<CustomColumnType>('text');
	const [isRequired, setIsRequired] = useState(false);
	const [isActive, setIsActive] = useState(true);
	const [options, setOptions] = useState<string[]>([]);
	const [newOption, setNewOption] = useState('');
	const [validation, setValidation] = useState<CustomColumnValidation>({});

	// errors
	const [errors, setErrors] = useState<Record<string, string>>({});

	// reset form when dialog opens/closes or column changes
	useEffect(() => {
		if (open) {
			if (column) {
				setName(column.name);
				setType(column.type);
				setIsRequired(column.isRequired);
				setIsActive(column.isActive);
				setOptions(column.options || []);
				setValidation(column.validation || {});
			} else {
				setName('');
				setType('text');
				setIsRequired(false);
				setIsActive(true);
				setOptions([]);
				setValidation({});
			}
			setNewOption('');
			setErrors({});
		}
	}, [open, column]);

	// handlers
	const handleAddOption = () => {
		const trimmedOption = newOption.trim();
		if (trimmedOption && !options.includes(trimmedOption)) {
			setOptions([...options, trimmedOption]);
			setNewOption('');
		}
	};

	const handleRemoveOption = (optionToRemove: string) => {
		setOptions(options.filter((opt) => opt !== optionToRemove));
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleAddOption();
		}
	};

	const validate = (): boolean => {
		const newErrors: Record<string, string> = {};

		if (!name.trim()) {
			newErrors.name = t('nameRequired');
		}

		if (type === 'select' && options.length < 2) {
			newErrors.options = t('spendPlan.selectRequiresAtLeastTwoOptions');
		}

		if (type === 'number') {
			if (
				validation.min !== undefined &&
				validation.max !== undefined &&
				validation.min > validation.max
			) {
				newErrors.validation = t('spendPlan.minCannotBeGreaterThanMax');
			}
		}

		if (type === 'text') {
			if (
				validation.minLength !== undefined &&
				validation.maxLength !== undefined &&
				validation.minLength > validation.maxLength
			) {
				newErrors.validation = t('spendPlan.minLengthCannotBeGreaterThanMaxLength');
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = () => {
		if (!validate()) return;

		const columnData: Omit<CustomColumnDefinition, 'id' | 'order'> = {
			isActive,
			isRequired,
			name: name.trim(),
			type,
		};

		// Add options for select type
		if (type === 'select' && options.length > 0) {
			columnData.options = options;
		}

		// Add validation if any rules are set
		const hasValidation = Object.values(validation).some(
			(v) => v !== undefined && v !== null && v !== ''
		);
		if (hasValidation) {
			columnData.validation = validation;
		}

		onSave(columnData);
	};

	const getTypeLabel = (columnType: CustomColumnType): string => {
		const labels: Record<CustomColumnType, string> = {
			boolean: t('spendPlan.columnType.boolean'),
			date: t('spendPlan.columnType.date'),
			number: t('spendPlan.columnType.number'),
			select: t('spendPlan.columnType.select'),
			text: t('spendPlan.columnType.text'),
		};
		return labels[columnType] || columnType;
	};

	const getTypeDescription = (columnType: CustomColumnType): string => {
		const descriptions: Record<CustomColumnType, string> = {
			boolean: t('spendPlan.columnTypeDescription.boolean'),
			date: t('spendPlan.columnTypeDescription.date'),
			number: t('spendPlan.columnTypeDescription.number'),
			select: t('spendPlan.columnTypeDescription.select'),
			text: t('spendPlan.columnTypeDescription.text'),
		};
		return descriptions[columnType] || '';
	};

	return (
		<Dialog fullWidth maxWidth="md" onClose={onClose} open={open}>
			<DialogTitle>
				<Stack alignItems="center" direction="row" justifyContent="space-between">
					<Typography variant="h3">
						{column ? t('spendPlan.editField') : t('spendPlan.addField')}
					</Typography>
					<IconButton onClick={onClose} size="small">
						<IconClose />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent dividers>
				<Grid container spacing={2}>
					{/* Column Name */}
					<Grid item xs={12}>
						<TextField
							autoFocus
							error={Boolean(errors.name)}
							fullWidth
							helperText={errors.name}
							label={t('spendPlan.fieldName')}
							onChange={(e) => setName(e.target.value)}
							required
							value={name}
						/>
					</Grid>

					{/* Column Type */}
					<Grid item xs={12}>
						<FormControl fullWidth>
							<InputLabel>{t('spendPlan.fieldType')}</InputLabel>
							<Select
								label={t('spendPlan.fieldType')}
								onChange={(e) => {
									setType(e.target.value as CustomColumnType);
									setValidation({});
								}}
								value={type}>
								{customColumnTypes.map((columnType) => (
									<MenuItem key={columnType} value={columnType}>
										{getTypeLabel(columnType)}
									</MenuItem>
								))}
							</Select>
							<FormHelperText>{getTypeDescription(type)}</FormHelperText>
						</FormControl>
					</Grid>

					{/* Options for Select type */}
					{type === 'select' && (
						<Grid item xs={12}>
							<Typography gutterBottom variant="subtitle2">
								{t('spendPlan.dropdownOptions')}
							</Typography>
							<Stack direction="row" spacing={1}>
								<TextField
									error={Boolean(errors.options)}
									fullWidth
									helperText={errors.options || t('spendPlan.pressEnterToAddOption')}
									label={t('addOption')}
									onChange={(e) => setNewOption(e.target.value)}
									onKeyDown={handleKeyDown}
									size="small"
									value={newOption}
								/>
								<Button
									disabled={!newOption.trim()}
									onClick={handleAddOption}
									variant="outlined">
									<IconAdd />
								</Button>
							</Stack>
							{options.length > 0 && (
								<Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{options.map((option) => (
										<Chip
											key={option}
											label={option}
											onDelete={() => handleRemoveOption(option)}
											size="small"
										/>
									))}
								</Box>
							)}
						</Grid>
					)}

					{/* Validation for Number type */}
					{type === 'number' && (
						<Grid item xs={12}>
							<Typography gutterBottom variant="subtitle2">
								{t('validation')}
							</Typography>
							<Grid container spacing={2}>
								<Grid item xs={6}>
									<TextField
										fullWidth
										label={t('spendPlan.minimumValue')}
										onChange={(e) =>
											setValidation({
												...validation,
												min: e.target.value
													? Number(e.target.value)
													: undefined,
											})
										}
										size="small"
										type="number"
										value={validation.min ?? ''}
									/>
								</Grid>
								<Grid item xs={6}>
									<TextField
										fullWidth
										label={t('spendPlan.maximumValue')}
										onChange={(e) =>
											setValidation({
												...validation,
												max: e.target.value
													? Number(e.target.value)
													: undefined,
											})
										}
										size="small"
										type="number"
										value={validation.max ?? ''}
									/>
								</Grid>
							</Grid>
							{errors.validation && (
								<FormHelperText error>{errors.validation}</FormHelperText>
							)}
						</Grid>
					)}

					{/* Validation for Text type */}
					{type === 'text' && (
						<Grid item xs={12}>
							<Typography gutterBottom variant="subtitle2">
								{t('validation')}
							</Typography>
							<Grid container spacing={2}>
								<Grid item xs={6}>
									<TextField
										fullWidth
										label={t('spendPlan.minimumLength')}
										onChange={(e) =>
											setValidation({
												...validation,
												minLength: e.target.value
													? Number(e.target.value)
													: undefined,
											})
										}
										size="small"
										type="number"
										value={validation.minLength ?? ''}
									/>
								</Grid>
								<Grid item xs={6}>
									<TextField
										fullWidth
										label={t('spendPlan.maximumLength')}
										onChange={(e) =>
											setValidation({
												...validation,
												maxLength: e.target.value
													? Number(e.target.value)
													: undefined,
											})
										}
										size="small"
										type="number"
										value={validation.maxLength ?? ''}
									/>
								</Grid>
								<Grid item xs={12}>
									<TextField
										fullWidth
										helperText={t('spendPlan.patternHelperText')}
										label={t('spendPlan.pattern')}
										onChange={(e) =>
											setValidation({
												...validation,
												pattern: e.target.value || undefined,
											})
										}
										placeholder="^[A-Z]{2}-\d{4}$"
										size="small"
										value={validation.pattern ?? ''}
									/>
								</Grid>
							</Grid>
							{errors.validation && (
								<FormHelperText error>{errors.validation}</FormHelperText>
							)}
						</Grid>
					)}

					{/* Required Toggle */}
					<Grid item xs={12}>
						<FormControlLabel
							control={
								<Switch
									checked={isRequired}
									onChange={(e) => setIsRequired(e.target.checked)}
								/>
							}
							label={
								<Box>
									<Typography>{t('required')}</Typography>
									<Typography color="text.secondary" variant="caption">
										{t('spendPlan.requiredFieldDescription')}
									</Typography>
								</Box>
							}
						/>
					</Grid>

					{/* Active Toggle */}
					<Grid item xs={12}>
						<FormControlLabel
							control={
								<Switch
									checked={isActive}
									onChange={(e) => setIsActive(e.target.checked)}
								/>
							}
							label={
								<Box>
									<Typography>{t('active')}</Typography>
									<Typography color="text.secondary" variant="caption">
										{t('spendPlan.activeFieldDescription')}
									</Typography>
								</Box>
							}
						/>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose}>{t('cancel')}</Button>
				<Button onClick={handleSave} variant="contained">
					{column ? t('save') : t('add')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
